import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription,
  DialogFooter 
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  School, 
  Users, 
  GraduationCap,
  Upload,
  Download,
  Plus,
  Trash2,
  BarChart3,
  Trophy,
  Loader2,
  FileSpreadsheet,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SchoolAdminDashboard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [school, setSchool] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  
  // Import state
  const [importFile, setImportFile] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [showImportDialog, setShowImportDialog] = useState(false);
  
  // New teacher form
  const [showAddTeacher, setShowAddTeacher] = useState(false);
  const [newTeacher, setNewTeacher] = useState({ email: '', username: '' });
  const [creatingTeacher, setCreatingTeacher] = useState(false);

  useEffect(() => {
    // Redirect if not a school admin
    if (user && user.role !== 'school_admin' && user.role !== 'admin') {
      toast.error('School admin access required');
      navigate('/dashboard');
      return;
    }
    
    if (token) {
      fetchData();
    }
  }, [token, user, navigate]);

  const fetchData = async () => {
    try {
      // Fetch school info
      const schoolRes = await fetch(`${API_URL}/api/school-admin/schools`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const schoolData = await schoolRes.json();
      if (schoolData.schools?.length > 0) {
        setSchool(schoolData.schools[0]);
        
        // Fetch detailed school info
        const detailRes = await fetch(`${API_URL}/api/school-admin/schools/${schoolData.schools[0].id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const detailData = await detailRes.json();
        setSchool(detailData);
      }
      
      // Fetch teachers
      const teachersRes = await fetch(`${API_URL}/api/school-admin/teachers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const teachersData = await teachersRes.json();
      setTeachers(teachersData.teachers || []);
      
      // Fetch analytics
      const analyticsRes = await fetch(`${API_URL}/api/school-admin/analytics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const analyticsData = await analyticsRes.json();
      setAnalytics(analyticsData);
      
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Failed to load school data');
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await fetch(`${API_URL}/api/school-admin/import/template`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'student_import_template.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('Template downloaded');
    } catch (err) {
      toast.error('Failed to download template');
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setImportFile(file);
    setValidationResult(null);
    setImportResult(null);
    
    // Validate the file
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await fetch(`${API_URL}/api/school-admin/import/validate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const result = await res.json();
      setValidationResult(result);
      setShowImportDialog(true);
    } catch (err) {
      toast.error('Failed to validate file');
    }
  };

  const handleImport = async () => {
    if (!importFile) return;
    
    setImporting(true);
    const formData = new FormData();
    formData.append('file', importFile);
    
    try {
      const res = await fetch(`${API_URL}/api/school-admin/import/students`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const result = await res.json();
      setImportResult(result);
      
      if (result.successful > 0) {
        toast.success(`Imported ${result.successful} students successfully`);
        fetchData(); // Refresh data
      }
      if (result.failed > 0) {
        toast.error(`${result.failed} students failed to import`);
      }
    } catch (err) {
      toast.error('Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleAddTeacher = async (e) => {
    e.preventDefault();
    setCreatingTeacher(true);
    
    try {
      const res = await fetch(
        `${API_URL}/api/school-admin/teachers?email=${encodeURIComponent(newTeacher.email)}&username=${encodeURIComponent(newTeacher.username)}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to create teacher');
      }
      
      const result = await res.json();
      toast.success(`Teacher created! Temporary password: ${result.temporary_password}`);
      setShowAddTeacher(false);
      setNewTeacher({ email: '', username: '' });
      fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCreatingTeacher(false);
    }
  };

  const handleDeactivateTeacher = async (teacherId) => {
    if (!confirm('Are you sure you want to deactivate this teacher?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/school-admin/teachers/${teacherId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Teacher deactivated');
        fetchData();
      }
    } catch (err) {
      toast.error('Failed to deactivate teacher');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 py-8 px-4" data-testid="school-admin-dashboard">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
              <School className="h-8 w-8 text-indigo-400" />
              School Admin Portal
            </h1>
            <p className="text-slate-400 mt-1">
              {school?.name || 'Manage your school'}
            </p>
          </div>
          {school && (
            <Badge className="bg-indigo-500/20 text-indigo-400 border-indigo-500/30 text-sm px-3 py-1">
              {analytics?.overview?.teacher_count || 0} Teachers • {analytics?.overview?.student_count || 0} Students
            </Badge>
          )}
        </div>

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="bg-slate-900 border-slate-800">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="teachers">Teachers</TabsTrigger>
            <TabsTrigger value="import">Bulk Import</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-4 text-center">
                  <GraduationCap className="h-8 w-8 text-blue-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{analytics?.overview?.teacher_count || 0}</p>
                  <p className="text-slate-400 text-sm">Teachers</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-4 text-center">
                  <Users className="h-8 w-8 text-emerald-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{analytics?.overview?.student_count || 0}</p>
                  <p className="text-slate-400 text-sm">Students</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-4 text-center">
                  <School className="h-8 w-8 text-purple-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{analytics?.overview?.class_count || 0}</p>
                  <p className="text-slate-400 text-sm">Classes</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-4 text-center">
                  <Trophy className="h-8 w-8 text-yellow-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{analytics?.overview?.total_points?.toLocaleString() || 0}</p>
                  <p className="text-slate-400 text-sm">Total Points</p>
                </CardContent>
              </Card>
            </div>

            {/* Classes List */}
            {school?.classes && school.classes.length > 0 && (
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-white">Active Classes</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {school.classes.map((cls) => (
                      <div key={cls.id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                        <div>
                          <p className="font-medium text-white">{cls.name}</p>
                          <p className="text-slate-400 text-sm">{cls.student_count} students</p>
                        </div>
                        <Badge variant="outline" className="border-slate-600">
                          {cls.student_count} enrolled
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Teachers Tab */}
          <TabsContent value="teachers">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-white">Teachers</CardTitle>
                  <CardDescription className="text-slate-400">
                    Manage teacher accounts for your school
                  </CardDescription>
                </div>
                <Button 
                  className="bg-indigo-600 hover:bg-indigo-500"
                  onClick={() => setShowAddTeacher(true)}
                  data-testid="add-teacher-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Teacher
                </Button>
              </CardHeader>
              <CardContent>
                {teachers.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    No teachers yet. Add your first teacher to get started.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {teachers.map((teacher) => (
                      <div 
                        key={teacher.id}
                        className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                            <GraduationCap className="h-5 w-5 text-blue-400" />
                          </div>
                          <div>
                            <p className="font-medium text-white">{teacher.username}</p>
                            <p className="text-slate-400 text-sm">{teacher.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge variant="outline" className="border-slate-600">
                            {teacher.class_count || 0} classes
                          </Badge>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                            onClick={() => handleDeactivateTeacher(teacher.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bulk Import Tab */}
          <TabsContent value="import">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Upload className="h-5 w-5 text-emerald-400" />
                  Bulk Student Import
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Import multiple students at once using a CSV file
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Instructions */}
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <h4 className="font-medium text-white mb-2">Instructions:</h4>
                  <ol className="list-decimal list-inside text-slate-400 text-sm space-y-1">
                    <li>Download the CSV template</li>
                    <li>Fill in student information (email, username, date of birth)</li>
                    <li>Optionally include class invite codes to auto-enroll</li>
                    <li>Upload the completed CSV file</li>
                    <li>Review validation results and confirm import</li>
                  </ol>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-4">
                  <Button 
                    variant="outline" 
                    className="border-slate-700"
                    onClick={downloadTemplate}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download Template
                  </Button>
                  
                  <Button 
                    className="bg-emerald-600 hover:bg-emerald-500"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="upload-csv-btn"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Upload CSV
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    className="hidden"
                    onChange={handleFileSelect}
                  />
                </div>

                {/* Import Result Summary */}
                {importResult && (
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <h4 className="font-medium text-white mb-3">Import Results</h4>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div className="p-3 bg-slate-700/50 rounded-lg">
                        <p className="text-2xl font-bold text-white">{importResult.total_rows}</p>
                        <p className="text-slate-400 text-sm">Total Rows</p>
                      </div>
                      <div className="p-3 bg-emerald-500/10 rounded-lg">
                        <p className="text-2xl font-bold text-emerald-400">{importResult.successful}</p>
                        <p className="text-slate-400 text-sm">Successful</p>
                      </div>
                      <div className="p-3 bg-red-500/10 rounded-lg">
                        <p className="text-2xl font-bold text-red-400">{importResult.failed}</p>
                        <p className="text-slate-400 text-sm">Failed</p>
                      </div>
                    </div>
                    
                    {importResult.errors?.length > 0 && (
                      <div className="mt-4">
                        <p className="text-red-400 text-sm mb-2">Errors:</p>
                        <div className="max-h-40 overflow-y-auto space-y-1">
                          {importResult.errors.map((err, i) => (
                            <div key={i} className="text-xs text-slate-400 bg-slate-800 p-2 rounded">
                              Row {err.row}: {err.errors?.join(', ')}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <div className="space-y-6">
              {/* Completion Rate */}
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-white">Portfolio Completion</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <div className="flex justify-between mb-2">
                        <span className="text-slate-400">Completion Rate</span>
                        <span className="text-white font-medium">
                          {analytics?.overview?.completion_rate || 0}%
                        </span>
                      </div>
                      <div className="h-3 bg-slate-800 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-emerald-500 rounded-full transition-all"
                          style={{ width: `${analytics?.overview?.completion_rate || 0}%` }}
                        />
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-white">
                        {analytics?.overview?.portfolios_complete || 0}
                      </p>
                      <p className="text-slate-400 text-sm">
                        of {analytics?.overview?.student_count || 0} complete
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Top Students */}
              {analytics?.top_students?.length > 0 && (
                <Card className="bg-slate-900 border-slate-800">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Trophy className="h-5 w-5 text-yellow-400" />
                      Top Performers
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {analytics.top_students.map((student, i) => (
                        <div 
                          key={student.user_id}
                          className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                              i === 0 ? 'bg-yellow-500 text-black' :
                              i === 1 ? 'bg-slate-400 text-black' :
                              i === 2 ? 'bg-amber-600 text-black' :
                              'bg-slate-700 text-white'
                            }`}>
                              {i + 1}
                            </span>
                            <span className="font-medium text-white">{student.username}</span>
                          </div>
                          <Badge className="bg-emerald-500/20 text-emerald-400">
                            {student.total_points?.toLocaleString()} pts
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Teacher Dialog */}
      <Dialog open={showAddTeacher} onOpenChange={setShowAddTeacher}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white">
          <DialogHeader>
            <DialogTitle>Add New Teacher</DialogTitle>
            <DialogDescription className="text-slate-400">
              Create a teacher account. They will receive a temporary password.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddTeacher} className="space-y-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={newTeacher.email}
                onChange={(e) => setNewTeacher({ ...newTeacher, email: e.target.value })}
                placeholder="teacher@school.edu"
                className="bg-slate-800 border-slate-700"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Username</Label>
              <Input
                type="text"
                value={newTeacher.username}
                onChange={(e) => setNewTeacher({ ...newTeacher, username: e.target.value })}
                placeholder="ProfSmith"
                className="bg-slate-800 border-slate-700"
                required
                minLength={3}
                maxLength={30}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setShowAddTeacher(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={creatingTeacher} className="bg-indigo-600 hover:bg-indigo-500">
                {creatingTeacher ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Teacher'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import Validation Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-emerald-400" />
              Import Validation
            </DialogTitle>
          </DialogHeader>
          
          {validationResult && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="p-3 bg-slate-800/50 rounded-lg">
                  <p className="text-xl font-bold text-white">{validationResult.total_rows}</p>
                  <p className="text-slate-400 text-xs">Total Rows</p>
                </div>
                <div className="p-3 bg-emerald-500/10 rounded-lg">
                  <p className="text-xl font-bold text-emerald-400">{validationResult.valid_rows}</p>
                  <p className="text-slate-400 text-xs">Valid</p>
                </div>
                <div className="p-3 bg-red-500/10 rounded-lg">
                  <p className="text-xl font-bold text-red-400">{validationResult.invalid_rows}</p>
                  <p className="text-slate-400 text-xs">Invalid</p>
                </div>
              </div>

              {/* Status */}
              {validationResult.is_valid ? (
                <div className="flex items-center gap-2 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-emerald-400" />
                  <span className="text-emerald-400">File is valid and ready to import</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <XCircle className="h-5 w-5 text-red-400" />
                  <span className="text-red-400">File has errors that need to be fixed</span>
                </div>
              )}

              {/* Errors List */}
              {validationResult.errors?.length > 0 && (
                <div className="max-h-48 overflow-y-auto space-y-2">
                  {validationResult.errors.slice(0, 10).map((err, i) => (
                    <div key={i} className="text-sm p-2 bg-red-900/20 border border-red-900/30 rounded">
                      <span className="text-red-400">Row {err.row}:</span>
                      <span className="text-slate-400 ml-2">{err.issues?.join(', ')}</span>
                    </div>
                  ))}
                  {validationResult.errors.length > 10 && (
                    <p className="text-slate-500 text-sm">
                      ... and {validationResult.errors.length - 10} more errors
                    </p>
                  )}
                </div>
              )}

              {/* Preview */}
              {validationResult.preview?.length > 0 && (
                <div>
                  <p className="text-slate-400 text-sm mb-2">Preview (first {validationResult.preview.length} rows):</p>
                  <div className="text-xs space-y-1">
                    {validationResult.preview.map((row) => (
                      <div key={row.row} className="p-2 bg-slate-800/50 rounded flex justify-between">
                        <span className="text-white">{row.username}</span>
                        <span className="text-slate-400">{row.email}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowImportDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleImport}
              disabled={!validationResult?.is_valid || importing}
              className="bg-emerald-600 hover:bg-emerald-500"
            >
              {importing ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              Import {validationResult?.valid_rows || 0} Students
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SchoolAdminDashboard;
